import { useState } from 'react'

import { BiTestTube } from 'react-icons/bi'

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  FormLabel,
  HStack,
  Icon,
  Input,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react'

import { ClickToCopyButton } from '@/components/layouts/Copy/ClickToCopyButton'
import { Modal } from '@/components/overlays'
import { Button } from '@/components/ui'
import { useTestRulesLazyQuery } from '@/graphql/generated/hooks'

interface TestRuleModalIProps {
  isOpen: boolean
  onClose: () => void
  ruleId: string
  ruleName: string
}

export const TestRuleModal = ({
  isOpen,
  onClose,
  ruleId,
  ruleName,
}: TestRuleModalIProps) => {
  const [displayId, setDisplayId] = useState('')
  const [uuid, setUuid] = useState('')

  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [testRules, { data, loading: isLoading, error }] =
    useTestRulesLazyQuery()

  const hasDisplayId = displayId.trim().length > 0
  const hasUuid = uuid.trim().length > 0
  const isSubmitEnabled = hasDisplayId !== hasUuid // exactly one is filled

  const handleDisplayIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayId(e.target.value)
  }

  const handleUuidChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUuid(e.target.value)
  }

  const handleSubmit = () => {
    setHasSubmitted(true)
    testRules({
      variables: {
        displayId: hasDisplayId ? displayId.trim() : undefined,
        incidentId: hasUuid ? uuid.trim() : undefined,
      },
    })
  }

  const handleClose = () => {
    setDisplayId('')
    setUuid('')
    setHasSubmitted(false)
    onClose()
  }

  const matchedRules = data?.testRules ?? null
  const hasMatchedRules =
    hasSubmitted && !isLoading && !error && matchedRules !== null
  const didCurrentRuleMatch = matchedRules?.some((r) => r.id === ruleId)

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={handleClose}
      size='2xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <ModalHeader>
          <Box alignItems='center' display='flex' m='20px auto'>
            <Box display='grid' placeItems='center'>
              <Icon as={BiTestTube} boxSize='28px' color='#353849' />
            </Box>
            <Box
              color='#353849'
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              ml={3}
            >
              Test Automation
            </Box>
            <Spacer />
            <ModalCloseButton color='#000' opacity={0.4} position='initial' />
          </Box>
        </ModalHeader>

        <ModalBody pb={4} px={6}>
          <Alert borderRadius='md' marginBottom={4} status='info'>
            <HStack spacing={2}>
              <AlertIcon />
              <VStack alignItems='start' spacing={0}>
                <AlertDescription>
                  Tool only visible to Implementers with "@hivewatch" in email
                </AlertDescription>
              </VStack>
            </HStack>
          </Alert>

          <Text color='gray.600' fontSize='sm' mb={4}>
            Enter either an Incident Display ID or Incident UUID to test whether
            it would match <strong>{ruleName}</strong>.
          </Text>

          <Box mb={4}>
            <FormLabel fontSize='sm' fontWeight='semibold'>
              Incident Display ID
            </FormLabel>
            <Input
              isDisabled={hasUuid}
              onChange={handleDisplayIdChange}
              placeholder='e.g. INC-1234'
              value={displayId}
            />
          </Box>

          <Box mb={4}>
            <FormLabel fontSize='sm' fontWeight='semibold'>
              Incident UUID
            </FormLabel>
            <Input
              isDisabled={hasDisplayId}
              onChange={handleUuidChange}
              placeholder='e.g. 550e8400-e29b-41d4-a716-446655440000'
              value={uuid}
            />
          </Box>

          {error && (
            <Alert borderRadius='lg' mb={4} status='error'>
              <AlertIcon />
              <Box fontWeight='bold'>
                {error?.message ?? 'Something went wrong. Please try again.'}
              </Box>
            </Alert>
          )}

          {hasMatchedRules && (
            <Box mt={4}>
              {didCurrentRuleMatch ? (
                <Alert borderRadius='lg' status='success'>
                  <AlertIcon />
                  <Box>
                    <Box as='span' fontWeight='bold'>
                      Your Automation matched this Incident.
                    </Box>
                  </Box>
                </Alert>
              ) : (
                <Alert borderRadius='lg' status='info'>
                  <AlertIcon />
                  <Box>
                    <Box as='span' fontWeight='bold'>
                      Your Automation did not match this Incident.
                    </Box>
                  </Box>
                </Alert>
              )}
              {matchedRules.length > 0 && (
                <Box mt={3}>
                  <Text fontSize='sm' fontWeight='semibold' mb={2}>
                    Matched Automations:
                  </Text>
                  {matchedRules.map((rule) => (
                    <Box
                      borderRadius='md'
                      fontSize='sm'
                      key={rule.id}
                      mb={1}
                      px={3}
                      py={2}
                    >
                      <Box alignItems='center' display='flex' gap={2}>
                        <Text fontWeight='medium'>{rule.name}</Text>
                        <ClickToCopyButton
                          ariaLabel='Copy automation name'
                          successMessage='Automation name copied'
                          textToCopy={rule.name}
                        />
                      </Box>
                      <Box alignItems='center' display='flex' gap={2} mt={1}>
                        <Text color='gray.500' fontSize='xs'>
                          {rule.id}
                        </Text>
                        <ClickToCopyButton
                          ariaLabel='Copy automation ID'
                          successMessage='Automation ID copied'
                          textToCopy={rule.id}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </ModalBody>

        <ModalFooter gap={2}>
          <Button onClick={handleClose} variant='secondary'>
            Cancel
          </Button>
          <Button
            isDisabled={!isSubmitEnabled}
            isLoading={isLoading}
            loadingText='Testing...'
            onClick={handleSubmit}
            variant='primary'
          >
            Test Incident
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
